import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useTranslation } from 'react-i18next';
import useNotify from '../../hooks/useNotify';
import {
    createSeatingGroup,
    updateSeatingGroup,
    deleteSeatingGroup,
} from '@openapi/seating-groups/seating-groups';
import { getBookings } from '@openapi/bookings/bookings';

function SeatingGroupModal({
    open,
    onClose,
    bookingId,
    timeslotId,
    seatingGroupId,
    seatingGroupNote,
    allGroups,
    bookingGroups,
    onRefresh,
}) {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const [searchQuery, setSearchQuery] = useState('');
    const [allBookings, setAllBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedBookingIds, setSelectedBookingIds] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isDisbanding, setIsDisbanding] = useState(false);
    const [groupName, setGroupName] = useState(
        () => allGroups?.find((g) => g.id === seatingGroupId)?.name || '',
    );

    const currentGroup = allGroups?.find((g) => g.id === seatingGroupId) || null;
    const memberBookingIds = currentGroup?.bookingIds || [];
    // 1-based position of this group within the timeslot, used as the label
    // fallback ("Group 2") when the group has no name.
    const currentOrdinal =
        [...(allGroups || [])]
            .sort((a, b) => a.id - b.id)
            .findIndex((g) => g.id === seatingGroupId) + 1;

    // The parent mounts this modal fresh per open (and keys it by booking), so
    // state already initializes empty - just load the candidate bookings once.
    useEffect(() => {
        loadAllBookings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAllBookings = async () => {
        setIsLoading(true);
        try {
            const result = await getBookings({ optionalId: timeslotId, page: 0, pageSize: 10000 });
            setAllBookings(result?.bookings || []);
        } catch (err) {
            notifyError(err);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredResults = allBookings.filter((b) => {
        if (b.id === bookingId) return false;
        if (memberBookingIds.includes(b.id)) return false;
        // Already linked into another seating group - the backend rejects these,
        // so hide them rather than surface a confusing error on submit.
        if (b.seatingGroupId != null) return false;
        if (b.status === 'cancelled') return false;
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const name = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
        const code = (b.confirmationCode || '').toLowerCase();
        return name.includes(q) || code.includes(q) || String(b.id).includes(q);
    });

    const getBookingName = (bId) => {
        const group = bookingGroups?.find((g) => g.bookingId === bId);
        if (!group) return `#${bId}`;
        const first = group.booking?.firstName || '';
        const last = group.booking?.lastName || '';
        const name = `${first} ${last}`.trim();
        return name ? `${name} (#${bId})` : `#${bId}`;
    };

    const toggleSelectResult = (bId) => {
        setSelectedBookingIds((prev) =>
            prev.includes(bId) ? prev.filter((id) => id !== bId) : [...prev, bId],
        );
    };

    const handleCreateGroup = async () => {
        if (selectedBookingIds.length === 0) return;
        setIsSaving(true);
        try {
            await createSeatingGroup({
                timeslotId,
                bookingIds: [bookingId, ...selectedBookingIds],
                name: groupName.trim() || undefined,
            });
            notifySuccess(t('admin.seatingGroup.groupCreated'));
            onRefresh();
            onClose();
        } catch (err) {
            notifyError(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddToGroup = async () => {
        if (selectedBookingIds.length === 0) return;
        setIsSaving(true);
        try {
            await updateSeatingGroup(seatingGroupId, { addBookingIds: selectedBookingIds });
            notifySuccess(t('admin.seatingGroup.membersAdded'));
            setSelectedBookingIds([]);
            setSearchQuery('');
            onRefresh();
        } catch (err) {
            notifyError(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveMember = async (bId) => {
        setIsSaving(true);
        try {
            await updateSeatingGroup(seatingGroupId, { removeBookingIds: [bId] });
            notifySuccess(t('admin.seatingGroup.memberRemoved'));
            onRefresh();
        } catch (err) {
            notifyError(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRename = async () => {
        setIsSaving(true);
        try {
            await updateSeatingGroup(seatingGroupId, { name: groupName.trim() || null });
            notifySuccess(t('admin.seatingGroup.nameSaved'));
            onRefresh();
        } catch (err) {
            notifyError(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDisband = async () => {
        setIsDisbanding(true);
        try {
            await deleteSeatingGroup(seatingGroupId);
            notifySuccess(t('admin.seatingGroup.disbandSuccess'));
            onRefresh();
            onClose();
        } catch (err) {
            notifyError(err);
        } finally {
            setIsDisbanding(false);
        }
    };

    const hasGroup = !!seatingGroupId;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {hasGroup
                    ? currentGroup?.name ||
                      t('admin.seatingGroup.modalTitleExisting', { n: currentOrdinal })
                    : t('admin.seatingGroup.modalTitleNew')}
            </DialogTitle>
            <DialogContent>
                {seatingGroupNote && (
                    <Box
                        sx={{
                            mb: 2,
                            px: 1.5,
                            py: 1,
                            bgcolor: '#fff8e1',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'warning.light',
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {t('admin.seatingGroup.guestNote')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {seatingGroupNote}
                        </Typography>
                    </Box>
                )}

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    {t('admin.seatingGroup.nameLabel')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={t('admin.seatingGroup.namePlaceholder')}
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        inputProps={{ maxLength: 60 }}
                    />
                    {hasGroup && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRename}
                            disabled={isSaving || groupName.trim() === (currentGroup?.name || '')}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0, height: 40 }}
                        >
                            {t('admin.seatingGroup.saveName')}
                        </Button>
                    )}
                </Stack>

                {hasGroup && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            {t('admin.seatingGroup.currentMembers')}
                        </Typography>
                        <Stack spacing={0.5} sx={{ mb: 2 }}>
                            {memberBookingIds.map((bId) => (
                                <Stack
                                    key={bId}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <GroupsIcon fontSize="small" color="action" />
                                        <Typography variant="body2">
                                            {getBookingName(bId)}
                                        </Typography>
                                        {bId === bookingId && (
                                            <Chip
                                                size="small"
                                                label={t('admin.seatingGroup.thisBooking')}
                                                variant="outlined"
                                                sx={{ height: 18, fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </Stack>
                                    {memberBookingIds.length > 2 && bId !== bookingId && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveMember(bId)}
                                            disabled={isSaving}
                                            title={t('admin.seatingGroup.removeMember')}
                                        >
                                            <PersonRemoveIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                    </>
                )}

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    {t('admin.seatingGroup.searchPlaceholderLabel')}
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('admin.seatingGroup.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                {isLoading ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <SearchIcon fontSize="small" />
                                )}
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 1.5 }}
                />

                {!isLoading && filteredResults.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {t('admin.seatingGroup.noResults')}
                    </Typography>
                )}

                {filteredResults.length > 0 && (
                    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                        {filteredResults.map((b) => {
                            const name = `${b.firstName || ''} ${b.lastName || ''}`.trim();
                            const isSelected = selectedBookingIds.includes(b.id);
                            const wantsSeat = !!b.seatingGroupRequest;
                            const bGroupNote = b.seatingGroupNote;
                            return (
                                <Box
                                    key={b.id}
                                    onClick={() => toggleSelectResult(b.id)}
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        border: '1px solid',
                                        borderColor: isSelected
                                            ? 'primary.main'
                                            : wantsSeat
                                              ? 'warning.main'
                                              : 'divider',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        bgcolor: isSelected
                                            ? 'action.selected'
                                            : wantsSeat
                                              ? '#fff8e1'
                                              : 'background.paper',
                                        '&:hover': { borderColor: 'primary.light' },
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        {wantsSeat && (
                                            <PeopleAltIcon
                                                fontSize="small"
                                                sx={{ color: 'warning.dark', flexShrink: 0 }}
                                            />
                                        )}
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {name || `#${b.id}`}
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ ml: 1 }}
                                            >
                                                #{b.id}
                                                {b.confirmationCode && ` · ${b.confirmationCode}`}
                                            </Typography>
                                        </Typography>
                                    </Stack>
                                    {bGroupNote && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'warning.dark',
                                                display: 'block',
                                                mt: 0.25,
                                                pl: wantsSeat ? 3 : 0,
                                            }}
                                        >
                                            &ldquo;{bGroupNote}&rdquo;
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 1,
                    '& > :not(:first-of-type)': { marginLeft: 0 },
                }}
            >
                {!hasGroup ? (
                    <Button
                        variant="contained"
                        onClick={handleCreateGroup}
                        disabled={selectedBookingIds.length === 0 || isSaving}
                        fullWidth
                    >
                        {t('admin.seatingGroup.createGroup')}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleAddToGroup}
                        disabled={selectedBookingIds.length === 0 || isSaving}
                        fullWidth
                    >
                        {t('admin.seatingGroup.addMembers')}
                    </Button>
                )}
                {hasGroup && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDisband}
                        disabled={isDisbanding || isSaving}
                        fullWidth
                    >
                        {t('admin.seatingGroup.disband')}
                    </Button>
                )}
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSaving || isDisbanding}
                    fullWidth
                >
                    {t('common.cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default SeatingGroupModal;
